import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { resolveInitialLanguage } from '../lib/i18n.js';

export type Locale = 'en' | 'fr' | 'de' | 'es';
export type Currency = 'USD' | 'EUR' | 'GBP';

export interface PreferencesStore {
  locale: Locale;
  currency: Currency;
  setLocale: (locale: Locale) => void;
  setCurrency: (currency: Currency) => void;
}

const VALID_LOCALES = new Set<string>(['en', 'fr', 'de', 'es']);
const VALID_CURRENCIES = new Set<string>(['USD', 'EUR', 'GBP']);

function defaultLocale(): Locale {
  return resolveInitialLanguage(globalThis.navigator?.language) as Locale;
}

export const PREFERENCES_STORAGE_KEY = 'ab.preferences';

export const usePreferencesStore = create<PreferencesStore>()(
  persist(
    (set) => ({
      locale: defaultLocale(),
      currency: 'USD',
      setLocale: (locale) => set({ locale }),
      setCurrency: (currency) => set({ currency }),
    }),
    {
      name: PREFERENCES_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Defensive rehydration: reset tampered values to default rather than crash.
      merge: (persisted, current) => {
        const safe = { ...current };
        if (persisted && typeof persisted === 'object') {
          const p = persisted as Partial<PreferencesStore>;
          if (typeof p.locale === 'string' && VALID_LOCALES.has(p.locale)) {
            safe.locale = p.locale as Locale;
          }
          if (typeof p.currency === 'string' && VALID_CURRENCIES.has(p.currency)) {
            safe.currency = p.currency as Currency;
          }
        }
        return safe;
      },
    },
  ),
);
